import type { ListBuilderConfiguration } from '../types';

/**
 * Client Ecommerce User list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const CLIENT_ECOMMERCE_USER_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-client-ecommerce-users',
			cacheTTL: 600000,
			name: 'Client Ecommerce Users',
			type: 'default',
			active: true,
			defaultSortId: 'firstName-asc',
			columns: [
				{
					id: 'name',
					headers: [{ id: 'name-header', text: 'Name' }],
					fields: [
						{
							id: 'name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'public_user',
							},
							dataConfig: {
								type: 'template',
								template: '{firstName} {lastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					minWidth: 150,
					verticalAlignment: 'top',
				},
				{
					// PD-17191 review: the phone sits under the email in one column, as
					// on the ecommerce Login History. PD-16471: many storefront users sign
					// in by phone and have no email, so the phone is always shown.
					id: 'email',
					headers: [
						{ id: 'email-header', text: 'Email' },
						{ id: 'email-break', text: '', displayStyle: 'line_break' },
						{ id: 'phoneNumber-header', text: 'Phone' },
					],
					fields: [
						{
							id: 'email-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'email', objectTable: 'public_user' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'email-phone-break',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'sm', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'phoneNumber-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'phoneNumber', objectTable: 'public_user' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'phone',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'public_user',
							fieldName: 'email',
							direction: 'ASC',
						},
					],
					minWidth: 190,
					verticalAlignment: 'top',
				},
				{
					id: 'orderApprovalLimit',
					headers: [
						{ id: 'orderApprovalLimit-header', text: 'Order Approval Limit' },
					],
					fields: [
						{
							id: 'orderApprovalLimit-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'orderApprovalLimit',
									objectTable: 'client_public_user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'currency',
						},
					],
					sortable: true,
					width: 180,
					verticalAlignment: 'top',
				},
				{
					id: 'roles',
					headers: [{ id: 'roles-header', text: 'Role' }],
					fields: [
						{
							id: 'roles-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'roleNames', objectTable: 'public_user' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								valueMapping: [
									{ value: 'Ecommerce Customer', displayText: 'Buyer' },
									{ value: 'Ecommerce Admin', displayText: 'Admin' },
								],
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 120,
					verticalAlignment: 'top',
				},
				{
					// PD-17191 — this user's latest storefront activity for THIS
					// client, or a green Online now pill while they are on a
					// storefront for it. Both are attached server-side
					// (EcommerceUsersService.getClientEcommerceUsers); the tab
					// refetches quietly every minute so the pill appears and clears.
					id: 'lastActive',
					headers: [{ id: 'lastActive-header', text: 'Last Active' }],
					fields: [
						{
							id: 'lastActive-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'lastActiveDate',
									objectTable: 'client_public_user',
								},
								defaultValue: 'No storefront activity',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'custom',
							customComponent: { id: 'storefront-last-active' },
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'client_public_user',
							fieldName: 'lastActiveDate',
							direction: 'DESC',
						},
					],
					width: 160,
					verticalAlignment: 'top',
				},
				{
					id: 'enabled',
					headers: [{ id: 'enabled-header', text: 'Enabled' }],
					fields: [
						{
							id: 'enabled-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'isEnabled',
									objectTable: 'client_public_user',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'conditional',
									conditionalRules: [
										{
											condition: 'equals',
											value: true,
											color: { type: 'theme', themeColor: 'success' },
										},
										{
											condition: 'equals',
											value: false,
											color: { type: 'theme', themeColor: 'textSecondary' },
										},
									],
								},
								valueMapping: [
									{ value: true, displayText: 'Yes' },
									{ value: false, displayText: 'No' },
								],
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					width: 100,
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					headers: [{ id: 'actions-header', text: '' }],
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							// Every action here changes the login or its access to this
							// account, and the server refuses each one without
							// ecommerce_users:update, so the menu hides them from people
							// who only hold ecommerce_users:read (PD-16728).
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'editUser',
									requiredPermission: 'ecommerce_users:update',
								},
								{
									id: 'send-password-reset-action',
									label: 'Send Password Reset Message',
									iconName: 'envelope',
									callbackName: 'sendPasswordReset',
									requiredPermission: 'ecommerce_users:update',
								},
								{
									id: 'enable-action',
									label: 'Enable',
									iconName: 'check-circle',
									callbackName: 'toggleEnabled',
									requiredPermission: 'ecommerce_users:update',
									conditionalVisibility: {
										field: 'isEnabled',
										condition: 'equals',
										value: false,
									},
								},
								{
									id: 'disable-action',
									label: 'Disable',
									iconName: 'ban',
									callbackName: 'toggleEnabled',
									requiredPermission: 'ecommerce_users:update',
									conditionalVisibility: {
										field: 'isEnabled',
										condition: 'equals',
										value: true,
									},
								},
								{
									id: 'remove-action',
									label: 'Remove',
									iconName: 'trash',
									callbackName: 'removeUser',
									requiredPermission: 'ecommerce_users:update',
									danger: true,
								},
							],
						},
					],
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
					width: 140,
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	tableSorts: [
		{
			id: 'firstName-asc',
			name: 'First Name (A-Z)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'firstName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'firstName-desc',
			name: 'First Name (Z-A)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'firstName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'lastName-asc',
			name: 'Last Name (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'lastName', objectTable: 'public_user' },
			],
		},
		{
			id: 'lastName-desc',
			name: 'Last Name (Z-A)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'email-asc',
			name: 'Email (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'email', objectTable: 'public_user' },
			],
		},
		{
			id: 'email-desc',
			name: 'Email (Z-A)',
			fields: [
				{ direction: 'DESC', fieldName: 'email', objectTable: 'public_user' },
			],
		},
		{
			id: 'lastActive-desc',
			name: 'Last Active (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastActiveDate',
					objectTable: 'client_public_user',
				},
			],
		},
	],
	objects: [
		{
			baseUrl: '/ecommerce-users/:id',
			objectLabel: 'Ecommerce Users',
			objectTable: 'public_user',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'email', fieldLabel: 'Email' },
				{ fieldName: 'firstName', fieldLabel: 'First Name' },
				{ fieldName: 'lastName', fieldLabel: 'Last Name' },
				{ fieldName: 'roleNames', fieldLabel: 'Roles' },
				{ fieldName: 'orderApprovalLimit', fieldLabel: 'Order Approval Limit' },
				{ fieldName: 'isEnabled', fieldLabel: 'Enabled' },
				// PD-17191 — computed per row for this client, not a column.
				{ fieldName: 'lastActiveDate', fieldLabel: 'Last Active' },
			],
		},
	],
};
